/*
 * demo-utils — a small shared toolkit for doc-page live examples.
 *
 * The "when in doubt" defaults, in one place so every demo is consistent instead of each one
 * hand-rolling (or forgetting) them:
 *   - a sun that actually CASTS shadows,
 *   - a test-pattern GROUND that catches them (far more legible than a flat colour),
 *   - the standard orbit camera (six lines every demo repeated),
 *   - a moving/rotating prop, so the dynamics are visible at a glance.
 *
 * Demo-only — NOT part of the published `tosijs-3d` library. Registered in `demo/site.ts` under the
 * key `demo-utils`, so an example can `import { demoSun, orbitCam, patternGround, spinner } from 'demo-utils'`.
 *
 * ⚠️ UNVERIFIED until a full build runs (authored while the machine was out of memory). The element
 * factories (demoSun/patternGround) and the camera use APIs confirmed from existing demos; the
 * shadow-caster registration in `spinner` (via the b3d element's `register`) is the one spot to
 * eyeball once it builds.
 */
import * as BABYLON from '@babylonjs/core'
import {
  b3dSun,
  b3dSkybox,
  b3dLight,
  b3dGround,
  SvgTexture,
  sceneDelta,
  extractChunk,
  attachBiomePlugin,
  createCarry,
  createInventory,
  inventoryDock,
  b3dPanel,
} from '../src/index'
import type { InventoryDock, B3dPanel, InventoryApi } from '../src/index'

// The b3d element examples receive as `el` in sceneCreated — just the bits these helpers touch.
type B3dEl = {
  scene: BABYLON.Scene
  setActiveCamera(camera: BABYLON.Camera): void
  querySelector(selector: string): Element | null
  register?(additions: { meshes?: BABYLON.AbstractMesh[] }): void
}

/*
The doc site's PRIVATE helper layer.

The publishable subset now lives in `src/demo-utils.ts` and ships as
`tosijs-3d/demo-utils`, so the code a reader copies off a page resolves in
their project. This file re-exports those and adds the parts that are
deliberately NOT published:

- `TEST_PATTERN` / `TEST_GRID` — root-absolute paths that resolve only on THIS
  site. Publishing them would be a broken promise in a consumer's project.
- `volumetricDemo` — an SDF-lattice x-ray for one page.
- `impactMarker` — a demo effect, and a filed perf finding.

Plus a `pattern: boolean` convenience on the ground helpers, which maps onto
the published `texture` option using our own asset.
*/
export { orbitCam }
export {
  demoSun,
  spinner,
  flightStage,
  type DemoHost,
  type DemoSunOptions,
  type OrbitCamOptions,
  type SpinnerOptions,
  type FlightStageOptions,
} from '../src/demo-utils'
import {
  orbitCam,
  demoStage as publicDemoStage,
  patternGround as publicPatternGround,
  type DemoStageOptions,
  type PatternGroundOptions,
} from '../src/demo-utils'

/** Site-only: `pattern: true` selects OUR muted grid asset. */
export function patternGround(
  opts: PatternGroundOptions & { pattern?: boolean } = {}
) {
  const { pattern, ...rest } = opts
  return publicPatternGround(pattern ? { ...rest, texture: TEST_GRID } : rest)
}

/** Site-only: same `pattern` convenience, spread into a scene. */
export function demoStage(opts: DemoStageOptions & { pattern?: boolean } = {}) {
  const { pattern, ...rest } = opts
  return publicDemoStage(pattern ? { ...rest, texture: TEST_GRID } : rest)
}

/**
 * The library's Warhol-ish test pattern (in `static/`), reused for grounds and props.
 *
 * Exported because it is the answer to "what do I put on this so I can SEE it" —
 * a flat colour hides the lighting, the shadows and the UV mapping all at once,
 * which is how a demo ends up looking broken when it is merely bland.
 */
export const TEST_PATTERN = '/tosi-test-pattern.svg'

/**
 * The MUTED variant, for ground planes.
 *
 * Same grid, desaturated. The vivid pattern is right when the texture is the
 * SUBJECT (an svg-plane demo, a spinning prop) and wrong under one: a floor in
 * full Warhol competes with whatever is standing on it, which is why several
 * demos read as a colour chart with a small object in the middle. Tonio made
 * this variant for exactly that — "more muted, which might make it better as
 * the default ground material for test scenes".
 */
export const TEST_GRID = '/tosi-warhol-testgrid.svg'

export interface VolumetricDemoOptions {
  /** World extent of the tile, metres. */
  size?: number
  /** Lattice spacing, metres. Cost scales as the CUBE of this — 3–5 is sane. */
  spacing?: number
  /** Cells of lattice below / above y=0. Must bracket the surface AND the carves. */
  below?: number
  above?: number
  /** The heightfield. Solid below it, air above, before any carving. */
  ground: (x: number, z: number) => number
  /** Air volumes subtracted from the ground — `carve.*`, positive inside the air. */
  carves?: Array<(x: number, y: number, z: number) => number>
  /**
   * Depth (m) over which volcanism ramps to full. 0 = no depth ramp. A
   * function is re-read on every `rebuild`, so a slider can drive it.
   *
   * Bigger is SLOWER. It interacts with the shader's stage ladder in a way
   * worth knowing: verticals lag one stage behind horizontals by design (a
   * cliff face drains and crusts over; lava pools flat), so a cut face at half
   * volcanism shows COLD dark voronoi'd basalt, not glowing seams. To get a
   * glowing web low down you need the deep end to reach FULL volcanism — so a
   * slow ramp plus enough depth, rather than a fast ramp.
   */
  molten?: number | (() => number)
  /** Also build the heightfield mesh, as a red wireframe overlay. */
  reference?: boolean
  /** Orbit angle (radians). Default frames the tile from -z; a cutaway that
   * removes z>centre wants the camera on the -z side to face the section. */
  alpha?: number
  seaLevel?: number
}

export interface VolumetricDemo {
  /** The extracted mesh, rebuilt by `rebuild()`. */
  mesh: BABYLON.Mesh | null
  /** The flat-grey heightfield mesh, when `reference` is set. */
  referenceMesh: BABYLON.Mesh | null
  /** A `<div>` reporting triangles, time and deviation — append it next to the scene. */
  readout: HTMLDivElement
  /** Re-extract (after changing a carve, a slider, anything). */
  rebuild: () => void
  /** Swap the carve list and re-extract — the "punch a hole in it" control. */
  setCarves: (
    carves: Array<(x: number, y: number, z: number) => number>
  ) => void
  /** Show/hide either surface. */
  show: (which: 'both' | 'volumetric' | 'reference') => void
  /** Wireframe on both, from one flag so they cannot diverge. */
  wireframe: (on: boolean) => void
}

/**
 * Build the volumetric-tile scene every sdf demo shares.
 *
 * Call it from `sceneCreated` (it needs a live scene). The camera is set up via
 * `orbitCam`, which builds its OWN camera rather than adjusting the default one
 * — the default's zoom is clamped to 50 m, which silently pins any scene-scale
 * demo far too close.
 */
export function volumetricDemo(
  el: B3dEl,
  opts: VolumetricDemoOptions
): VolumetricDemo {
  const {
    size = 256,
    spacing = 4,
    below = 20,
    above = 30,
    ground,
    carves: initialCarves = [],
    molten = 0,
    reference = false,
    seaLevel = -200,
    alpha = -Math.PI / 2,
  } = opts

  const scene = el.scene
  const readout = document.createElement('div')
  readout.style.cssText =
    'font: 12px ui-monospace, Menlo, monospace; padding: 6px; color: #93a4b0'

  let mesh: BABYLON.Mesh | null = null
  let referenceMesh: BABYLON.Mesh | null = null
  let wire = false

  let carves = initialCarves
  const density = (x: number, y: number, z: number) => {
    let d = y - ground(x, z) // solid below the surface
    for (const c of carves) d = Math.max(d, c(x, y, z))
    return d
  }

  const rebuild = () => {
    mesh?.dispose()
    const cells = Math.max(1, Math.round(size / spacing))
    const t0 = performance.now()
    const m = extractChunk(
      density,
      { ix: 0, iy: -below, iz: 0, nx: cells, ny: below + above, nz: cells },
      { spacing, jitter: 0.2, seed: 4 }
    )
    const ms = performance.now() - t0

    mesh = new BABYLON.Mesh('volumetric', scene)
    const vd = new BABYLON.VertexData()
    vd.positions = Array.from(m.positions)
    vd.indices = Array.from(m.indices)
    vd.normals = Array.from(m.normals)

    // Depth below the surface → the biome shader's volcanism ramp, carried in
    // the colour alpha (inverted: 1 = none). The same channel b3d-terrain uses
    // for local volcanic provinces, so a cut face reads as molten interior.
    const moltenDepth = typeof molten === 'function' ? molten() : molten
    if (moltenDepth > 0) {
      const colours: number[] = []
      for (let i = 0; i < m.vertexCount; i++) {
        const x = m.positions[i * 3]
        const y = m.positions[i * 3 + 1]
        const z = m.positions[i * 3 + 2]
        const depth = Math.max(0, ground(x, z) - y)
        colours.push(1, 1, 1, 1 - Math.min(1, depth / moltenDepth))
      }
      vd.colors = colours
    }
    vd.applyToMesh(mesh)

    const mat = new BABYLON.StandardMaterial('vmat', scene)
    mat.diffuseColor = new BABYLON.Color3(0.5, 0.52, 0.5)
    mat.backFaceCulling = false
    mat.wireframe = wire
    attachBiomePlugin(mat, { seaLevel })
    mesh.material = mat
    if (moltenDepth > 0) mesh.useVertexColors = true

    // Deviation from the heightfield, which is the number these demos exist to
    // show: the volumetric surface should match what a grid tile would draw.
    let max = 0
    for (let i = 0; i < m.vertexCount; i++) {
      const d = Math.abs(
        m.positions[i * 3 + 1] -
          ground(m.positions[i * 3], m.positions[i * 3 + 2])
      )
      if (d > max) max = d
    }
    readout.textContent =
      `${m.triangleCount} triangles · extracted in ${ms.toFixed(
        0
      )}ms at ${spacing}m spacing` +
      (carves.length === 0
        ? ` · max deviation from the heightfield ${max.toFixed(3)}m`
        : ' · carved')
  }

  if (reference) {
    const subs = Math.max(1, Math.round(size / spacing))
    const pos: number[] = []
    const idx: number[] = []
    for (let iz = 0; iz <= subs; iz++)
      for (let ix = 0; ix <= subs; ix++) {
        const x = (ix / subs) * size
        const z = (iz / subs) * size
        pos.push(x, ground(x, z), z)
      }
    for (let iz = 0; iz < subs; iz++)
      for (let ix = 0; ix < subs; ix++) {
        const a = iz * (subs + 1) + ix
        // Winding matters: reversed, the normals point DOWN and it renders black.
        idx.push(a, a + 1, a + subs + 1, a + 1, a + subs + 2, a + subs + 1)
      }
    referenceMesh = new BABYLON.Mesh('heightfield', scene)
    const vd = new BABYLON.VertexData()
    vd.positions = pos
    vd.indices = idx
    const nrm: number[] = []
    BABYLON.VertexData.ComputeNormals(pos, idx, nrm)
    vd.normals = nrm
    vd.applyToMesh(referenceMesh)
    // A RED WIREFRAME, not a solid surface. A solid reference occludes exactly
    // what you are trying to see — a bore's mouths sit in the heightfield's
    // unbroken surface, so showing both hid the tunnel completely. As a
    // wireframe it overlays the comparison instead of covering it, and the
    // tessellation is visible too.
    const rm = new BABYLON.StandardMaterial('hmat', scene)
    rm.emissiveColor = new BABYLON.Color3(0.9, 0.15, 0.15)
    rm.diffuseColor = new BABYLON.Color3(0, 0, 0)
    rm.specularColor = new BABYLON.Color3(0, 0, 0)
    rm.backFaceCulling = false
    rm.wireframe = true
    referenceMesh.material = rm
  }

  rebuild()

  // Its own camera, framing the tile — the scene's default is clamped to 50m.
  orbitCam(el, {
    alpha,
    radius: size * 1.6,
    beta: 1.1,
    target: [size / 2, ground(size / 2, size / 2) * 0.4, size / 2],
  })

  const api: VolumetricDemo = {
    get mesh() {
      return mesh
    },
    get referenceMesh() {
      return referenceMesh
    },
    readout,
    rebuild,
    setCarves: (next) => {
      carves = next
      rebuild()
    },
    show: (which) => {
      mesh?.setEnabled(which !== 'reference')
      referenceMesh?.setEnabled(which !== 'volumetric')
    },
    wireframe: (on) => {
      wire = on
      if (mesh?.material)
        (mesh.material as BABYLON.StandardMaterial).wireframe = on
      if (referenceMesh?.material)
        (referenceMesh.material as BABYLON.StandardMaterial).wireframe = on
    },
  }
  return api
}

/**
 * A HIT MARKER: a disc lying flat on the surface at `point`, plus a stalk along
 * `normal`. Fades out and disposes itself.
 *
 * The disc is what makes a normal legible — a bare line tells you the direction
 * but not what it is perpendicular TO, and on an irregular surface that is the
 * whole question. Orientation comes from `Quaternion.FromUnitVectorsToRef`,
 * which is the one-liner for "rotate +Y onto this vector" and avoids the
 * gimbal case a yaw/pitch construction hits when the normal points straight up.
 *
 * It fades on `sceneDelta`, so it FREEZES with the rest of the world when the
 * scene is paused rather than quietly ageing out behind the pause panel.
 */
export function impactMarker(
  el: B3dEl,
  point: BABYLON.Vector3,
  normal: BABYLON.Vector3,
  opts: { life?: number; size?: number; color?: string } = {}
): void {
  const { life = 1.2, size = 0.35, color = '#ffd34d' } = opts
  const scene = el.scene
  const tint = BABYLON.Color3.FromHexString(color)

  const disc = BABYLON.MeshBuilder.CreateDisc(
    'impact-disc',
    { radius: size, tessellation: 16 },
    scene
  )
  // A disc is born in the XY plane facing +Z; rotate +Z onto the normal, then
  // lift it a hair off the surface so it doesn't z-fight what it just hit.
  disc.rotationQuaternion = BABYLON.Quaternion.FromUnitVectorsToRef(
    new BABYLON.Vector3(0, 0, 1),
    normal,
    new BABYLON.Quaternion()
  )
  disc.position = point.add(normal.scale(0.012))

  const stalk = BABYLON.MeshBuilder.CreateCylinder(
    'impact-normal',
    { height: size * 2.4, diameter: size * 0.14 },
    scene
  )
  stalk.rotationQuaternion = BABYLON.Quaternion.FromUnitVectorsToRef(
    new BABYLON.Vector3(0, 1, 0),
    normal,
    new BABYLON.Quaternion()
  )
  stalk.position = point.add(normal.scale(size * 1.2))

  const mat = new BABYLON.StandardMaterial('impact-mat', scene)
  mat.emissiveColor = tint
  mat.diffuseColor = tint
  mat.disableLighting = true
  mat.backFaceCulling = false
  disc.material = mat
  stalk.material = mat

  let age = 0
  const tick = () => {
    age += sceneDelta(scene)
    const k = 1 - age / life
    if (k <= 0) {
      scene.onBeforeRenderObservable.removeCallback(tick)
      disc.dispose()
      stalk.dispose()
      mat.dispose()
      return
    }
    mat.alpha = k
    disc.scaling.setAll(1 + (1 - k) * 0.8) // a small outward pop
  }
  scene.onBeforeRenderObservable.add(tick)
}

/**
 * A person's inventory (a loadout and a bag) on the scene's SYSTEM BAR: a
 * button (a standing figure) that opens it under the bar, flat and in a headset. Put
 * `panels()` inside the `<tosi-b3d>`.
 *
 *   const loadout = demoLoadout()
 *   b3d({}, …, ...loadout.panels())
 */
export function demoLoadout(
  opts: {
    /** Slots filled at the start, `{ right_side: 'pistol' }` (out of the bag). */
    equip?: Record<string, string>
  } = {}
): {
  panels: () => B3dPanel[]
  dock: () => InventoryDock | null
  /** The inventory itself, to watch (`subscribe`) and move things in. */
  inv: InventoryApi
} {
  const kind = (id: string, tags: string[], mass: number) => ({
    id,
    name: id,
    tags,
    mass,
  })
  const kinds = {
    helm: kind('helm', ['helmet'], 2),
    goggles: kind('goggles', ['face'], 0.5),
    cuirass: kind('cuirass', ['chest'], 8),
    pack: kind('pack', ['back'], 1),
    belt: kind('belt', ['waist'], 0.5),
    knife: kind('knife', ['side', 'hand'], 1),
    pistol: kind('pistol', ['side', 'hand'], 1),
    greaves: kind('greaves', ['legs'], 3),
    boots: kind('boots', ['feet'], 2),
    sword: kind('sword', ['hand'], 3),
    rifle: kind('rifle', ['hand', 'two-handed'], 4),
    shield: kind('shield', ['hand'], 5),
  }
  const slot = (...accepts: string[]) => ({ accepts, holds: null })
  const body = {
    type: 'slots' as const,
    slots: {
      helmet: slot('helmet'),
      face: slot('face'),
      chest: slot('chest'),
      shoulder: slot('shoulder'),
      back: slot('back'),
      waist: slot('waist'),
      left_side: slot('side'),
      right_side: slot('side'),
      legs: slot('legs'),
      feet: slot('feet'),
      hand_left: slot('hand'),
      hand_right: slot('hand'),
    },
  }
  const items: Record<string, { id: string; kind: string; qty: number }> = {}
  for (const k of Object.keys(kinds)) items[k] = { id: k, kind: k, qty: 1 }
  const worn = opts.equip ?? {}
  for (const [slotName, id] of Object.entries(worn))
    (body.slots as Record<string, { holds: string | null }>)[slotName].holds =
      id
  const inv = createInventory(
    {
      items,
      holders: {
        hero: {
          id: 'hero',
          containers: {
            body,
            bag: {
              type: 'list',
              holds: Object.keys(items).filter(
                (id) => !Object.values(worn).includes(id)
              ),
            },
          },
        },
      },
    } as any,
    {
      kinds,
      rules: [
        ({ doc, kinds: ks }: any) => {
          const s = doc.holders.hero.containers.body.slots
          const hands = [s.hand_left.holds, s.hand_right.holds]
          const two = hands.find(
            (id: string | null) =>
              id && ks[doc.items[id].kind].tags.includes('two-handed')
          )
          return two && hands[0] && hands[1]
            ? ['two-handed: both hands must be free']
            : []
        },
      ],
    } as any
  )
  let dock: InventoryDock | null = null
  const ready: Promise<InventoryDock> = Promise.all([
    fetch('/person-loadout.svg').then((r) => r.text()),
    fetch('/item-icons.json').then((r) => r.json()),
  ]).then(([art, icons]) => {
    for (const k of Object.keys(kinds))
      (kinds as Record<string, { icon?: string }>)[k].icon = icons[k]
    dock = inventoryDock({
      inv,
      carry: createCarry(),
      holder: 'hero',
      title: 'hero',
      schematic: { container: 'body', art },
      list: 'bag',
      alsoIn: (_item, k, at) =>
        k?.tags?.includes('two-handed') && at.slot?.startsWith('hand_')
          ? [at.slot === 'hand_left' ? 'hand_right' : 'hand_left']
          : [],
      // Nowhere to put it down in this scene: it is simply gone (a game puts
      // it on the ground here).
      handleDrop: (item) => console.log('dropped', item.kind),
    })
    // Laid out on the page (text is measured there), out of sight.
    const park = document.createElement('div')
    park.style.cssText = 'position:fixed; left:-10000px; top:0'
    park.append(dock.svg)
    document.body.append(park)
    return dock
  })
  /*
  ON THE SYSTEM BAR, not pinned to the rig: a button (a standing figure) that
  opens it under the bar, flat and in a headset, with your bag beside it.
  The small closed picture it used to be over your shoulder is gone; the
  button is what reminds you it is there.
  */
  const pinned = b3dPanel({
    title: 'Inventory',
    icon: 'personStanding',
  }) as B3dPanel
  ready.then((d) => {
    pinned.svg = d.svg
  })
  return {
    inv,
    dock: () => dock,
    panels: () => [pinned],
  }
}
